import { observer, inject } from 'mobx-react';
import React from 'react';
import { Instance } from 'mobx-state-tree';
import moment from 'moment';
import { DutyAssignment } from '../../store/models/DutyAssignment';
import { Button, Checkbox, Colors, Intent, Popover } from '@blueprintjs/core';
import styled from '@emotion/styled';
import { SystemStatus } from '../../store/models/SystemStatus';
import config from '../../config';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { TLCode } from '../../common/enums';
import { AuthenticationStore, ModalStore, PharmacyStore } from '../../store';
import { ConfirmationDialog } from '../common/dialog/ConfirmationDialog';

const ContainerCheckbox = styled.div({
  '& .bp3-control': {
    marginBottom: '0px',
  },
});

interface DutySelectionProps {
  dutyList: Array<Instance<typeof DutyAssignment>>;
  onChange: (ids: number[]) => void;
  isInConflict: (id: number) => boolean;
  hasAlreadyDutyOn: (id: number) => boolean;
  checkTooCloseDuties?: (id: number) => any;
  systemStatus?: Array<Instance<typeof SystemStatus>>;
  pharmacyUser: boolean;
  disable?: boolean;
  selectedDuties: number[];
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  ModalStore?: Instance<typeof ModalStore>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  showPharmacyName?: boolean;
  sortByDate?: boolean;
}

interface DutySelectionState {
  tooCloseDialogOpen: boolean;
  tooCloseMessage: string;
  tooCloseCanBypass: boolean;
  pendingDutyAssignmentId?: number;
}

@inject('AuthenticationStore')
@inject('ModalStore')
@inject('PharmacyStore')
@observer
class DutySelection extends React.Component<DutySelectionProps & WithTranslateProps, DutySelectionState> {
  state: DutySelectionState = {
    tooCloseDialogOpen: false,
    tooCloseMessage: '',
    tooCloseCanBypass: false,
    pendingDutyAssignmentId: undefined,
  };

  onCheckboxChange = async (dutyAssignmentId) => {
    //si la ph a déjà une garde, le changement est refusé
    const hasAlreadyDutyOnDate = await this.props.hasAlreadyDutyOn(dutyAssignmentId);
    if (hasAlreadyDutyOnDate) {
      this.props.ModalStore!.confirm.disableCancel(true);
      await this.props.ModalStore!.confirm.show(
        this.props.translate(TLCode.GENERAL_Warning),
        this.props.translate(TLCode.EXCHANGE_ConflictDutyDate),
        false,
      );
      return;
    }

    const getTooCloseDuties = await this.props.checkTooCloseDuties!(dutyAssignmentId);
    if (getTooCloseDuties.length > 0) {
      const canBypass = this.props.AuthenticationStore!.isSecretariat || this.props.AuthenticationStore!.isSuperAdmin;

      this.setState({
        tooCloseDialogOpen: true,
        tooCloseCanBypass: canBypass,
        tooCloseMessage:
          (canBypass
            ? this.props.translate(TLCode.EXCHANGE_ConflictDutyTooClose_ByPass)
            : this.props.translate(TLCode.EXCHANGE_ConflictDutyTooClose)) + getTooCloseDuties[0],
        pendingDutyAssignmentId: dutyAssignmentId,
      });
      return;
    }

    await this.proceedWithSelection(dutyAssignmentId);
  };

  // Once a "too close" warning has been acknowledged (and bypassed when allowed),
  // run the remaining conflict check and toggle the duty selection.
  proceedWithSelection = async (dutyAssignmentId) => {
    const conflicts = await this.props.isInConflict(dutyAssignmentId);
    if (
      !conflicts ||
      (await this.props.ModalStore!.confirm.show(
        this.props.translate(TLCode.GENERAL_Warning),
        this.props.translate(TLCode.EXCHANGE_ConflictSecretariat),
      ))
    ) {
      let { selectedDuties } = this.props;
      if (selectedDuties!.includes(dutyAssignmentId)) {
        selectedDuties = selectedDuties!.filter((id) => id !== dutyAssignmentId);
      } else {
        selectedDuties!.push(dutyAssignmentId);
      }
      this.props.onChange(selectedDuties);
    }
  };

  handleTooCloseContinue = () => {
    const { pendingDutyAssignmentId } = this.state;
    this.setState({ tooCloseDialogOpen: false, pendingDutyAssignmentId: undefined });
    if (pendingDutyAssignmentId !== undefined) {
      this.proceedWithSelection(pendingDutyAssignmentId);
    }
  };

  handleTooCloseCancel = () => {
    this.setState({ tooCloseDialogOpen: false, pendingDutyAssignmentId: undefined });
  };

  toggleColor = (color) => {
    if (color == Colors.WHITE) {
      return Colors.LIGHT_GRAY4;
    } else {
      return Colors.WHITE;
    }
  };

  render() {
    const { selectedDuties } = this.props;
    const dutyList = this.props.sortByDate
      ? [...this.props.dutyList].sort((a, b) => {
          const dateA = new Date(a.duty!.dutyDate.date);
          const dateB = new Date(b.duty!.dutyDate.date);
          return dateA > dateB ? 1 : dateA < dateB ? -1 : 0;
        })
      : this.props.dutyList;

    let prevRow: any = undefined;
    let backgroundColor = Colors.WHITE;
    const format = config.dateFormatWithDay ? config.dateFormatWithDay : 'ddd DD/MM/YYYY';
    return (
      <ContainerCheckbox>
        <ConfirmationDialog
          isOpen={this.state.tooCloseDialogOpen}
          title={this.props.translate(TLCode.GENERAL_Warning)}
          message={this.state.tooCloseMessage}
          onContinue={this.state.tooCloseCanBypass ? this.handleTooCloseContinue : this.handleTooCloseCancel}
          onCancel={this.state.tooCloseCanBypass ? this.handleTooCloseCancel : undefined}
          confirmText={this.props.translate(this.state.tooCloseCanBypass ? TLCode.GENERAL_Force : TLCode.GENERAL_Close)}
          confirmIntent={this.state.tooCloseCanBypass ? Intent.DANGER : undefined}
          cancelText={this.props.translate(TLCode.GENERAL_Cancel)}
        />
        {dutyList.map((d) => {
          if (prevRow) {
            if (prevRow!.duty!.dutyDate.date != d.duty!.dutyDate.date || prevRow!.pharmacyId != d.pharmacyId) {
              backgroundColor = this.toggleColor(backgroundColor);
            }
          }
          prevRow = d;
          const pharmacy = this.props.PharmacyStore!.dataList.find((p) => d.pharmacyId === p.pharmacyId);
          return (
            <div
              style={{ background: backgroundColor, display: 'flex', alignItems: 'center' }}
              key={d.dutyAssignmentId}
            >
              <Checkbox
                key={d.dutyAssignmentId}
                onChange={() => this.onCheckboxChange(d.dutyAssignmentId)}
                checked={selectedDuties.includes(d.dutyAssignmentId)}
                disabled={this.props.disable}
                style={{ marginBottom: 0, flexGrow: 1 }}
              >
                {this.props.showPharmacyName
                  ? pharmacy!.name + ' - ' + pharmacy!.city + ' (' + pharmacy!.zipCode + ') '
                  : ''}
                {d.duty!.dutyType!.name} - {moment(d.duty!.dutyDate.date).format(format)}
              </Checkbox>
              {this.props.showPharmacyName && pharmacy && (
                <Popover
                  content={
                    <div style={{ padding: '10px' }}>
                      <div style={{ fontWeight: 'bold' }}>
                        {pharmacy.apbNumber} - {pharmacy.name}
                      </div>
                      <div>
                        {pharmacy.address}, {pharmacy.zipCode} {pharmacy.city}
                      </div>
                      <div>Tél.: {pharmacy.phone || '—'}</div>
                    </div>
                  }
                  interactionKind="click"
                  position="right"
                >
                  <Button icon="info-sign" minimal small />
                </Popover>
              )}
            </div>
          );
        })}
      </ContainerCheckbox>
    );
  }
}

export default withTranslate(DutySelection);
